/**
 * Organizational Scope Selector
 * Reusable component for selecting a node from the hospital organizational hierarchy
 * Used by: Audit QMS, Risk Assessments, Protocol Applicability
 * 
 * Displays: Organization → Location → Building → Floor → Department → Room
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { doc, getDoc } from 'firebase/firestore';
import { ChevronRight, Building2, MapPin, Home, Layers, Briefcase, DoorOpen } from 'lucide-react';
import Modal from '../ui/Modal';
import { useOrg } from '../../hooks/useOrg';
import { db } from '../../firebase';
import './OrganizationalScopeSelector.css';

const OrganizationalScopeSelector = ({ 
  value, 
  onChange, 
  allowedTypes = ['hospital', 'location', 'building', 'floor', 'department', 'room'],
  disabled = false 
}) => {
  const { t } = useTranslation('common');
  const { activeOrgId, isLoading: orgContextLoading } = useOrg();
  const [expanded, setExpanded] = useState(false);
  const [hierarchy, setHierarchy] = useState(null);
  const [loading, setLoading] = useState(false);
  const [orgDocMissing, setOrgDocMissing] = useState(false);
  const containerRef = React.useRef(null);

  const loadHierarchy = useCallback(async () => {
    if (!activeOrgId) {
      setHierarchy(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const { organizationService, clinicalStructureService } = await import('../../services/firebaseService');
      
      const org = await organizationService.getById(activeOrgId);
      
      if (!org) {
        setHierarchy(null);
        setLoading(false);
        return;
      }

      const locations = await clinicalStructureService.getLocationsByOrganization(activeOrgId);

      const buildDeptNode = async (dept) => {
        const rooms = await clinicalStructureService.getRoomsByDepartment(dept.id);
        return {
          id: dept.id,
          label: dept.name,
          type: 'department',
          children: rooms.map((room) => ({
            id: room.id,
            label: room.name,
            type: 'room',
            children: []
          }))
        };
      };

      const buildBuildingNode = async (building) => {
        const floors = await clinicalStructureService.getFloorsByBuilding(building.id);
        let floorNodes;

        if (floors.length === 0) {
          const depts = await clinicalStructureService.getDepartmentsByBuilding(building.id);
          const children = await Promise.all(depts.map((d) => buildDeptNode(d)));
          floorNodes = [
            {
              id: `__nofloor__${building.id}`,
              label: '—',
              type: 'floor',
              children
            }
          ];
        } else {
          floorNodes = await Promise.all(
            floors.map(async (floor) => {
              const depts = await clinicalStructureService.getDepartmentsByFloor(floor.id);
              const children = await Promise.all(depts.map((d) => buildDeptNode(d)));
              return {
                id: floor.id,
                label: floor.name,
                type: 'floor',
                children
              };
            })
          );
        }

        return {
          id: building.id,
          label: building.name,
          type: 'building',
          children: floorNodes
        };
      };

      const locationNodes = await Promise.all(
        locations.map(async (loc) => {
          const buildings = await clinicalStructureService.getBuildingsByLocation(loc.id);
          const buildingChildren = await Promise.all(
            buildings.map((b) => buildBuildingNode(b))
          );
          return {
            id: loc.id,
            label: loc.name,
            type: 'location',
            children: buildingChildren
          };
        })
      );

      const tree = {
        id: org.id,
        label: org.name,
        type: 'hospital',
        children: locationNodes
      };

      if (!tree.children || tree.children.length === 0) {
        setHierarchy(null);
      } else {
        setHierarchy(tree);
      }
    } catch (err) {
      console.error('[OrganizationalScopeSelector] Error loading hierarchy:', err);
      setHierarchy(null);
      setOrgDocMissing(false);
    } finally {
      setLoading(false);
    }
  }, [activeOrgId]);

  useEffect(() => {
    if (!expanded) return;
    if (!activeOrgId) {
      setHierarchy(null);
      setLoading(false);
      return;
    }
    loadHierarchy();
  }, [activeOrgId, expanded, loadHierarchy]);

  const handleSelect = (node) => {
    if (!allowedTypes.includes(node.type)) {
      return;
    }
    
    // Build breadcrumb path from root to selected node
    const buildPath = (tree, targetId, path = []) => {
      if (tree.id === targetId) {
        return [...path, { label: tree.label, type: tree.type }];
      }
      if (tree.children) {
        for (const child of tree.children) {
          const result = buildPath(child, targetId, [...path, { label: tree.label, type: tree.type }]);
          if (result) return result;
        }
      }
      return null;
    };
    
    const breadcrumbPath = buildPath(hierarchy, node.id) || [{ label: node.label, type: node.type }];
    
    onChange({
      nodeId: node.id,
      nodeLabel: node.label,
      nodeType: node.type,
      breadcrumbPath
    });
    
    setExpanded(false);
  };

  const getIcon = (type) => {
    const icons = {
      hospital: Building2,
      location: MapPin,
      building: Home,
      floor: Layers,
      department: Briefcase,
      room: DoorOpen
    };
    return icons[type] || Building2;
  };

  const getTypeLabel = (type) => {
    const labels = {
      hospital: t('hospital') || 'Hospital',
      location: t('location') || 'Location',
      building: t('building') || 'Building',
      floor: t('floor') || 'Floor',
      department: t('department') || 'Department',
      room: t('room') || 'Room'
    };
    return labels[type] || type;
  };

  return (
    <>
      <div className="org-scope-selector" ref={containerRef}>
        <button
          type="button"
          className={`org-scope-trigger ${expanded ? 'expanded' : ''} ${disabled ? 'disabled' : ''}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (!disabled) {
              setExpanded(true);
            }
          }}
          disabled={disabled}
        >
          {value ? (
            <span className="org-scope-value">
              {React.createElement(getIcon(value.nodeType), { size: 16 })}
              {value.breadcrumbPath && value.breadcrumbPath.length > 1 ? (
                <span className="org-scope-breadcrumb">
                  {value.breadcrumbPath.map((crumb, idx) => (
                    <span key={idx}>
                      {idx > 0 && <ChevronRight size={12} className="breadcrumb-separator" />}
                      <span className={idx === value.breadcrumbPath.length - 1 ? 'breadcrumb-current' : 'breadcrumb-parent'}>
                        {crumb.label}
                      </span>
                    </span>
                  ))}
                </span>
              ) : (
                <>
                  <span className="org-scope-type">{getTypeLabel(value.nodeType)}</span>
                  <ChevronRight size={14} className="separator" />
                  <span className="org-scope-label">{value.nodeLabel}</span>
                </>
              )}
            </span>
          ) : (
            <span className="org-scope-placeholder">
              {t('selectOrganizationalScope') || 'Selectează nivelul organizațional...'}
            </span>
          )}
        </button>
      </div>

      {expanded && (
        <Modal
          isOpen={expanded}
          onClose={() => setExpanded(false)}
          title={t('selectOrganizationalScope') || 'Selectează nivelul organizațional'}
          size="medium"
        >
          <div className="org-scope-modal-content">
            {(loading || orgContextLoading) ? (
              <div className="org-scope-loading">{t('loading') || 'Se încarcă...'}</div>
            ) : !activeOrgId ? (
              <div className="org-scope-empty org-scope-empty--warning">
                {t('organizationalScopeNoActiveOrg')}
              </div>
            ) : orgDocMissing ? (
              <div className="org-scope-empty org-scope-empty--warning">
                {t('organizationalScopeOrgDocMissing')}
              </div>
            ) : hierarchy ? (
              <div className="org-scope-tree">
                <TreeNode 
                  node={hierarchy} 
                  onSelect={handleSelect} 
                  allowedTypes={allowedTypes}
                  selectedId={value?.nodeId}
                  depth={0}
                />
              </div>
            ) : (
              <div className="org-scope-empty">{t('noHierarchyData')}</div>
            )}
          </div>
        </Modal>
      )}
    </>
  );
};

const TreeNode = ({ node, onSelect, allowedTypes, selectedId, depth }) => {
  const [expanded, setExpanded] = useState(depth < 2);
  const hasChildren = node.children && node.children.length > 0;
  const isSelectable = allowedTypes.includes(node.type);
  const isSelected = selectedId === node.id;
  const Icon = getNodeIcon(node.type);

  return (
    <div className="org-tree-node" style={{ '--depth': depth }}>
      <div 
        className={`org-tree-node-row ${isSelectable ? 'selectable' : 'not-selectable'} ${isSelected ? 'selected' : ''}`}
        onClick={() => {
          if (isSelectable) onSelect(node);
        }}
      >
        {hasChildren && (
          <button
            type="button"
            className="org-tree-toggle"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setExpanded(!expanded);
            }}
          >
            {expanded ? '▼' : '▶'}
          </button>
        )}
        {!hasChildren && <span className="org-tree-spacer" />}
        
        <Icon size={16} className="org-tree-icon" />
        <span className="org-tree-label">{node.label}</span>
        {!isSelectable && (
          <span className="org-tree-disabled-hint">(not selectable)</span>
        )}
      </div>
      
      {hasChildren && expanded && (
        <div className="org-tree-children">
          {node.children.map((child) => (
            <TreeNode
              key={child.id}
              node={child}
              onSelect={onSelect}
              allowedTypes={allowedTypes}
              selectedId={selectedId}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
};

function getNodeIcon(type) {
  const icons = {
    hospital: Building2,
    location: MapPin,
    building: Home,
    floor: Layers,
    department: Briefcase,
    room: DoorOpen
  };
  return icons[type] || Building2;
}

export default OrganizationalScopeSelector;
